import { Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { Menu, CanAccess, useGetIdentity } from 'react-admin';
import PersonIcon from '@mui/icons-material/Person';
import TuneIcon from '@mui/icons-material/Tune';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import MenuBookIcon from '@mui/icons-material/MenuBook';

import { isAdministrator } from '@/components/filters/CreatedByUserFilter';
import { SettingsPage } from './SettingsPage';
import { SiteConfigPage } from '@/modules/site_config/SiteConfigPage';
import { MunicipalityList } from '@/modules/municipalities/MunicipalityList';
import { MunicipalityCreate } from '@/modules/municipalities/MunicipalityCreate';
import { MunicipalityEdit } from '@/modules/municipalities/MunicipalityEdit';
import { HelpDocumentList } from '@/modules/help_documents/HelpDocumentList';
import { HelpDocumentCreate } from '@/modules/help_documents/HelpDocumentCreate';
import { HelpDocumentEdit } from '@/modules/help_documents/HelpDocumentEdit';

const requirePermit = (element: JSX.Element) => (
    <CanAccess resource="permit" action="update">
        {element}
    </CanAccess>
);

const requireHelp = (element: JSX.Element) => (
    <CanAccess resource="help_documents" action="list">
        {element}
    </CanAccess>
);

export const SettingsLayout = () => {
    const { identity } = useGetIdentity();
    const isAdmin = isAdministrator(identity);
    return (
        <Box display="flex" gap={2} mt={1}>
            <Box sx={{ width: 220, flexShrink: 0 }}>
                <Menu>
                    <Menu.Item
                        to="/settings/profile"
                        primaryText="My info"
                        leftIcon={<PersonIcon />}
                    />
                    {isAdmin ? (
                        <Menu.Item
                            to="/settings/site-config"
                            primaryText="Site config"
                            leftIcon={<TuneIcon />}
                        />
                    ) : null}
                    <CanAccess resource="permit" action="update">
                        <Menu.Item
                            to="/settings/municipalities"
                            primaryText="Municipalities"
                            leftIcon={<LocationCityIcon />}
                        />
                    </CanAccess>
                    <CanAccess resource="help_documents" action="list">
                        <Menu.Item
                            to="/settings/help-knowledge"
                            primaryText="Help knowledge"
                            leftIcon={<MenuBookIcon />}
                        />
                    </CanAccess>
                </Menu>
            </Box>
            <Box flex={1} minWidth={0}>
                <Routes>
                    <Route index element={<Navigate to="/settings/profile" replace />} />
                    <Route path="profile" element={<SettingsPage />} />
                    <Route path="site-config" element={<SiteConfigPage />} />
                    <Route path="municipalities" element={requirePermit(<MunicipalityList />)} />
                    <Route
                        path="municipalities/create"
                        element={requirePermit(<MunicipalityCreate />)}
                    />
                    <Route
                        path="municipalities/:id"
                        element={requirePermit(<MunicipalityEdit />)}
                    />
                    <Route
                        path="help-knowledge"
                        element={requireHelp(<HelpDocumentList />)}
                    />
                    <Route
                        path="help-knowledge/create"
                        element={requireHelp(<HelpDocumentCreate />)}
                    />
                    <Route
                        path="help-knowledge/:id"
                        element={requireHelp(<HelpDocumentEdit />)}
                    />
                </Routes>
            </Box>
        </Box>
    );
};

SettingsLayout.path = '/settings/*';
